import { empty } from "ramda";
import { isUndefined } from "ramda-adjunct";
import { useCallback, useRef, useState } from "react";

/**
 * Type definition for the useEnabledInput hook props.
 */
type UseEnabledInputProps<TValue> = {
  /** Initial enabled state */
  initDisabled?: boolean;
  /** Initial value to store */
  initialValue: TValue;
  /** Current value */
  /** Callback function triggered when value changes */
  onChange: (val: TValue) => void;
} & (TValue extends string | any[] | Record<any, any>
  ? {
      /** Value to use when disabled */
      emptyValue?: TValue;
    }
  : {
      /** Value to use when disabled */
      emptyValue: TValue;
    });

/**
 * A custom hook that manages an enabled/disabled state paired with a value.
 * When enabled, it uses the stored value. When disabled, it uses an empty value or set empty value.
 */
export const useDisabledInput = <TValue,>({
  initDisabled = true,
  initialValue,
  onChange,
  emptyValue = undefined,
}: UseEnabledInputProps<TValue>): [boolean, (val: boolean) => void, (val: TValue) => void] => {
  const lastValue = useRef<TValue>(initialValue);
  const [disabled, setDisabled] = useState(initDisabled);

  /**
   * Handles changes to the enabled state
   */
  const handleChangeEnabled = useCallback(
    (isDisabled: boolean) => {
      setDisabled(isDisabled);
      if (isDisabled) {
        onChange(!isUndefined(emptyValue) ? emptyValue : empty(initialValue));
      } else {
        onChange(lastValue.current);
      }
    },
    [emptyValue, initialValue, onChange]
  );

  const handleChangeValue = useCallback(
    (val: TValue) => {
      lastValue.current = val;
      onChange(val);
    },
    [onChange]
  );

  return [disabled, handleChangeEnabled, handleChangeValue];
};
